import { useTranslation } from 'react-i18next';
import type { QuotaProfileAssignmentPreview } from '../../api.js';
import { toCredits } from '../../components/AllocationPanel.js';

/**
 * What a save would do, shown before it happens. A profile edit reaches
 * everyone on it at once, so this is the only place an operator can see the
 * blast radius — and the only reason the save button is gated on having read it.
 */
export function ImpactPreviewDialog({
  preview,
  acknowledged,
  busy,
  onAcknowledgeChange,
  onClose
}: {
  preview: QuotaProfileAssignmentPreview;
  acknowledged: boolean;
  busy: boolean;
  onAcknowledgeChange: (next: boolean) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const matchedTotal = preview.matched.reduce((sum, row) => sum + row.userCount, 0);

  return (
    <section className="panel quota-profile-preview" aria-live="polite">
      <h3>{t('impactPreview.title')}</h3>
      <ul>
        <li>{t('impactPreview.matched', { count: matchedTotal })}</li>
        <li>{t('impactPreview.fallback', { count: preview.fallbackCount })}</li>
        <li>{t('impactPreview.unassigned', { count: preview.unmappedCount })}</li>
        <li>{t('impactPreview.pinned', { count: preview.pinnedCount })}</li>
        <li>{t('impactPreview.evaluated', { count: preview.evaluatedCount })}</li>
      </ul>
      {preview.theoreticalExposureByCycle.length > 0 ? (
        <p className="muted">
          {preview.theoreticalExposureByCycle
            .map((row) =>
              t('impactPreview.exposureRow', {
                cycle: t(`creditLimits.cycle_${row.cycle}`),
                credits: toCredits(row.creditMillis)
              })
            )
            .join(' · ')}
        </p>
      ) : null}
      <p className="muted">{t('quotaProfiles.exposureWarning')}</p>
      {preview.fundedWarning ? (
        <p className="warning" role="alert">
          {t('impactPreview.fundedWarning', {
            exposure: toCredits(preview.fundedWarning.theoreticalExposureMillis),
            funded: toCredits(preview.fundedWarning.fundedRemainingMillis)
          })}
        </p>
      ) : null}
      {preview.truncated ? (
        // Never present a partial evaluation as the whole picture.
        <p className="warning" role="alert">
          {t('impactPreview.truncatedNote', { evaluated: preview.evaluatedCount })}
        </p>
      ) : null}
      {preview.wouldBlockCount > 0 ? (
        <>
          <p className="warning" role="alert">
            {t('impactPreview.wouldBlockNote', { count: preview.wouldBlockCount })}
          </p>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => onAcknowledgeChange(event.target.checked)}
              disabled={busy}
            />
            <span>{t('impactPreview.acknowledge')}</span>
          </label>
        </>
      ) : null}
      <button type="button" onClick={onClose} disabled={busy}>
        {t('impactPreview.close')}
      </button>
    </section>
  );
}
